import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type {
  BackupDestinationDto,
  BackupRestoreWorkflowDto,
  SubscriptionBackupSetDto,
  SubscriptionProtectionDto,
} from '@cpm/shared';
import { BACKUP_FREQUENCY_PRESETS, BACKUP_FREQUENCY_PRESET_CRON } from '@cpm/shared';
import { ArchiveRestore, Play, Save, ShieldCheck } from 'lucide-react';
import { useMemo, useState } from 'react';
import { api } from '../api/client.js';
import { useMe } from '../api/auth.js';
import {
  Badge,
  Button,
  Card,
  CardHeader,
  EmptyState,
  ErrorNote,
  Input,
  Label,
  Select,
  Spinner,
  Textarea,
  statusTone,
} from './ui.js';

function dateTime(value: string | null): string {
  return value ? new Date(value).toLocaleString() : 'never';
}

function setServer(set: SubscriptionBackupSetDto): string | null {
  return set.manifest.repositories.find((repository) => repository.status === 'succeeded')
    ?.serverUuid ?? null;
}

function verificationSummary(workflow: BackupRestoreWorkflowDto): string {
  const verification = workflow.verification;
  if (!verification) return workflow.error ?? 'Verification has not completed.';
  return [
    verification.hashesVerified ? 'hashes' : null,
    verification.databasesVerified ? 'database integrity' : null,
    verification.rowCountsVerified ? 'row counts' : null,
    verification.healthChecksVerified ? 'health checks' : null,
  ]
    .filter(Boolean)
    .join(', ');
}

function ScheduleEditor({
  subscriptionId,
  portal,
  data,
}: {
  subscriptionId: string;
  portal: boolean;
  data: SubscriptionProtectionDto;
}) {
  const queryClient = useQueryClient();
  const existing = data.schedules[0];
  const [frequency, setFrequency] = useState(existing?.frequency ?? 'daily');
  const [enabled, setEnabled] = useState(existing?.enabled ?? true);
  const [quiesceIds, setQuiesceIds] = useState<string[]>(existing?.quiesceApplicationIds ?? []);
  const destinations = useQuery({
    queryKey: ['backups', 'destinations'],
    queryFn: () =>
      api.get<{ destinations: BackupDestinationDto[] }>('/api/backups/destinations'),
    enabled: !portal,
  });
  const [selected, setSelected] = useState<string[]>(existing?.destinationIds ?? []);
  const route = portal
    ? `/api/portal/subscriptions/${subscriptionId}/protection-schedule`
    : `/api/backups/subscriptions/${subscriptionId}/protection-schedule`;
  const save = useMutation({
    mutationFn: () =>
      api.put(route, {
        frequency,
        enabled,
        ...(!portal ? { destinationIds: selected, quiesceApplicationIds: quiesceIds } : {}),
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: [portal ? 'portal-protection' : 'subscription-protection', subscriptionId],
      });
    },
  });

  if (!data.permissions.schedule) return null;
  return (
    <form
      className="space-y-3 rounded-lg border border-slate-200 p-3 dark:border-slate-800"
      onSubmit={(event) => {
        event.preventDefault();
        save.mutate();
      }}
    >
      <div className="grid gap-3 sm:grid-cols-[1fr_auto_auto] sm:items-end">
        <div>
          <Label htmlFor={`${portal ? 'portal' : 'staff'}-protection-frequency`}>Frequency</Label>
          <Select
            id={`${portal ? 'portal' : 'staff'}-protection-frequency`}
            value={frequency}
            onChange={(event) => setFrequency(event.target.value)}
          >
            {BACKUP_FREQUENCY_PRESETS.map((preset) => (
              <option key={preset} value={preset}>
                {preset.replace('_', ' ')} ({BACKUP_FREQUENCY_PRESET_CRON[preset]})
              </option>
            ))}
          </Select>
        </div>
        <label className="flex items-center gap-2 pb-2 text-sm">
          <input
            type="checkbox"
            checked={enabled}
            onChange={(event) => setEnabled(event.target.checked)}
          />
          Enabled
        </label>
        <Button
          type="submit"
          loading={save.isPending}
          disabled={!portal && selected.length === 0}
        >
          <Save className="size-4" /> Save schedule
        </Button>
      </div>
      {!portal && (
        <fieldset>
          <legend className="mb-2 text-sm font-medium">Destinations</legend>
          {destinations.isPending ? (
            <Spinner />
          ) : destinations.error ? (
            <ErrorNote error={destinations.error} />
          ) : destinations.data.destinations.filter((destination) => destination.enabled).length === 0 ? (
            <p className="text-sm text-amber-700 dark:text-amber-400">
              Configure an enabled destination on the Backups page first.
            </p>
          ) : (
            <div className="flex flex-wrap gap-3">
              {destinations.data.destinations
                .filter((destination) => destination.enabled)
                .map((destination) => (
                  <label key={destination.id} className="flex items-center gap-2 text-sm">
                    <input
                      type="checkbox"
                      checked={selected.includes(destination.id)}
                      onChange={(event) =>
                        setSelected((current) =>
                          event.target.checked
                            ? [...current, destination.id]
                            : current.filter((id) => id !== destination.id),
                        )
                      }
                    />
                    {destination.name} ({destination.type})
                  </label>
                ))}
            </div>
          )}
        </fieldset>
      )}
      {!portal && (data.applications?.length ?? 0) > 0 && (
        <fieldset className="space-y-2">
          <legend className="text-sm font-medium">Consistent application snapshots</legend>
          <p className="text-xs text-slate-500 dark:text-slate-400">
            Selected applications briefly stop while their persistent volumes are copied together,
            then restart before upload. Capture is limited to two minutes, with independent restart
            recovery after 150 seconds. Use applications that shut down cleanly and have no shared writers.
          </p>
          {data.applications!.map(application => (
            <label key={application.id} className="flex items-center gap-2 text-sm">
              <input type="checkbox" checked={quiesceIds.includes(application.id)}
                onChange={event => setQuiesceIds(current => event.target.checked
                  ? [...current, application.id] : current.filter(id => id !== application.id))} />
              Pause {application.name} for each backup
            </label>
          ))}
        </fieldset>
      )}
      {portal && (existing?.quiesceApplicationIds?.length ?? 0) > 0 && (
        <p className="text-xs text-slate-500">Your operator has configured brief application pauses for consistent backups.</p>
      )}
      {portal && (
        <p className="text-xs text-slate-500 dark:text-slate-400">
          Your operator controls the encrypted repository destinations; tenant credentials are
          never requested.
        </p>
      )}
      {save.error && <ErrorNote error={save.error} />}
    </form>
  );
}

function RestoreRequest({
  subscriptionId,
  portal,
  set,
  onClose,
}: {
  subscriptionId: string;
  portal: boolean;
  set: SubscriptionBackupSetDto;
  onClose: () => void;
}) {
  const queryClient = useQueryClient();
  const [healthChecks, setHealthChecks] = useState('');
  const serverUuid = setServer(set);
  const restore = useMutation({
    mutationFn: () =>
      api.post(
        `${portal ? '/api/portal' : '/api/backups'}/subscriptions/${subscriptionId}/restores`,
        {
          backupSetId: set.id,
          ...(portal
            ? {}
            : {
                targetMode: 'staging',
                targetServerUuid: serverUuid,
                targetProjectUuid: null,
              }),
          healthCheckUrls: healthChecks
            .split('\n')
            .map((value) => value.trim())
            .filter(Boolean),
        },
      ),
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: [portal ? 'portal-protection' : 'subscription-protection', subscriptionId],
      });
      onClose();
    },
  });

  return (
    <form
      className="space-y-4"
      onSubmit={(event) => {
        event.preventDefault();
        restore.mutate();
      }}
    >
      <p className="text-sm text-slate-600 dark:text-slate-300">
        Restore set <code>{set.id.slice(0, 8)}</code> into an isolated staging path on{' '}
        <code>{serverUuid ?? 'an unavailable server'}</code>. Production is not changed.
      </p>
      <div>
        <Label htmlFor="restore-health-checks">Application health-check URLs</Label>
        <Textarea
          id="restore-health-checks"
          rows={4}
          value={healthChecks}
          onChange={(event) => setHealthChecks(event.target.value)}
          placeholder={'https://staging.example.test/health\nhttps://staging.example.test/ready'}
        />
        <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">
          One URL per line. Volume-bearing applications require a health check before the restore
          can be promoted.
        </p>
      </div>
      {restore.error && <ErrorNote error={restore.error} />}
      <div className="flex justify-end gap-3">
        <Button type="button" variant="secondary" onClick={onClose}>
          Cancel
        </Button>
        <Button type="submit" loading={restore.isPending} disabled={!serverUuid}>
          <ArchiveRestore className="size-4" /> Request staging restore
        </Button>
      </div>
    </form>
  );
}

export function SubscriptionProtectionPanel({
  subscriptionId,
  portal = false,
}: {
  subscriptionId: string;
  portal?: boolean;
}) {
  const me = useMe();
  const queryClient = useQueryClient();
  const [restoreSet, setRestoreSet] = useState<SubscriptionBackupSetDto | null>(null);
  const queryKey = [portal ? 'portal-protection' : 'subscription-protection', subscriptionId];
  const protection = useQuery({
    queryKey,
    queryFn: () =>
      api.get<SubscriptionProtectionDto>(
        `${portal ? '/api/portal' : '/api/backups'}/subscriptions/${subscriptionId}/protection`,
      ),
    retry: false,
  });
  const trigger = useMutation({
    mutationFn: () =>
      api.post(
        `${portal ? '/api/portal' : '/api/backups'}/subscriptions/${subscriptionId}/backup-now`,
        {},
      ),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey }),
  });
  const retry = useMutation({
    mutationFn: (set: SubscriptionBackupSetDto) =>
      api.post(`/api/backups/sets/${set.id}/retry`, {}),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey }),
  });
  const data = protection.data;
  const promotableSafetySets = useMemo(
    () =>
      data?.sets
        .filter((set) => set.status === 'succeeded')
        .sort((left, right) => right.createdAt.localeCompare(left.createdAt)) ?? [],
    [data?.sets],
  );
  const promote = useMutation({
    mutationFn: ({
      workflow,
      safetySet,
    }: {
      workflow: BackupRestoreWorkflowDto;
      safetySet: SubscriptionBackupSetDto;
    }) =>
      api.post(`/api/backups/restores/${workflow.id}/promote`, {
        confirmation: 'PROMOTE',
        prePromotionBackupSetId: safetySet.id,
      }),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey }),
  });

  if (protection.isPending) return <Spinner />;
  if (protection.error) return <ErrorNote error={protection.error} />;
  if (!data) return null;

  return (
    <Card>
      <CardHeader
        title="Subscription protection"
        description="Encrypted, portable application volumes, database dumps, and CPM configuration with restore verification"
        actions={
          data.permissions.trigger ? (
            <Button
              type="button"
              variant="secondary"
              loading={trigger.isPending}
              disabled={data.schedules.length === 0}
              onClick={() => trigger.mutate()}
            >
              <Play className="size-4" /> Back up now
            </Button>
          ) : undefined
        }
      />
      <div className="space-y-5 px-5 py-4">
        <div className="grid gap-3 text-sm sm:grid-cols-3">
          <div>
            <p className="text-slate-500 dark:text-slate-400">Recent restore test</p>
            <p className="font-medium">{dateTime(data.recentSuccessfulRestoreTestAt)}</p>
          </div>
          <div>
            <p className="text-slate-500 dark:text-slate-400">V2-G14 gate</p>
            <Badge tone={data.recentSuccessfulRestoreTestAt ? 'green' : 'yellow'}>
              {data.recentSuccessfulRestoreTestAt ? 'ready' : 'restore test required'}
            </Badge>
          </div>
          <div>
            <p className="text-slate-500 dark:text-slate-400">Production promotion</p>
            <p className="font-medium">
              {data.permissions.promote ? 'admin approval required' : 'operator only'}
            </p>
          </div>
        </div>

        <ScheduleEditor
          subscriptionId={subscriptionId}
          portal={portal}
          data={data}
        />

        {data.embeddedServiceWarnings.length > 0 && (
          <div className="rounded-lg bg-amber-50 p-3 text-sm text-amber-800 dark:bg-amber-500/10 dark:text-amber-300">
            <p className="font-medium">Embedded-service limitations</p>
            <ul className="mt-1 list-disc pl-5">
              {data.embeddedServiceWarnings.map((warning) => (
                <li key={warning}>{warning}</li>
              ))}
            </ul>
          </div>
        )}

        <div>
          <h3 className="mb-2 text-sm font-semibold">Backup sets</h3>
          {!data.permissions.inspect ? (
            <p className="text-sm text-slate-500 dark:text-slate-400">
              Your plan does not include backup inspection.
            </p>
          ) : data.sets.length === 0 ? (
            <EmptyState title="No subscription backup sets yet" />
          ) : (
            <ul className="divide-y divide-slate-100 text-sm dark:divide-slate-800">
              {data.sets.slice(0, 10).map((set) => (
                <li key={set.id} className="flex flex-wrap items-center justify-between gap-3 py-2">
                  <div>
                    <p className="flex items-center gap-2 font-medium">
                      <Badge tone={statusTone(set.status)}>{set.status}</Badge>
                      attempt {set.attempt} · {dateTime(set.createdAt)}
                    </p>
                    <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">
                      {set.manifest.components.filter((component) => component.status === 'succeeded').length}
                      /{set.manifest.components.length} components ·{' '}
                      {set.manifest.repositories.filter((repository) => repository.status === 'succeeded').length}
                      /{set.manifest.repositories.length} repositories
                    </p>
                    {(set.manifest.applicationCaptures?.length ?? 0) > 0 && (
                      <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">
                        {set.manifest.applicationCaptures!.length} application snapshot(s) captured and restarted ·{' '}
                        {set.manifest.applicationCaptures!.reduce((sum, capture) => sum + capture.sqliteFiles, 0)} SQLite file(s) checked
                      </p>
                    )}
                    {set.error && (
                      <p className="mt-1 text-xs text-red-600 dark:text-red-400">{set.error}</p>
                    )}
                  </div>
                  <div className="flex gap-2">
                    {data.permissions.trigger && ['partial', 'failed'].includes(set.status) && (
                      <Button
                        type="button"
                        variant="secondary"
                        loading={retry.isPending}
                        onClick={() => retry.mutate(set)}
                      >
                        <Play className="size-4" /> Retry
                      </Button>
                    )}
                    {data.permissions.requestRestore && set.status === 'succeeded' && (
                      <Button type="button" variant="secondary" onClick={() => setRestoreSet(set)}>
                        <ArchiveRestore className="size-4" /> Restore-test
                      </Button>
                    )}
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>

        {data.permissions.inspect && (
          <div>
            <h3 className="mb-2 text-sm font-semibold">Restore workflows</h3>
            {data.restores.length === 0 ? (
              <p className="text-sm text-slate-500 dark:text-slate-400">
                No staging restores requested.
              </p>
            ) : (
              <ul className="divide-y divide-slate-100 text-sm dark:divide-slate-800">
                {data.restores.slice(0, 10).map((workflow) => {
                  const safetySet = promotableSafetySets.find(
                    (set) => set.createdAt > workflow.createdAt,
                  );
                  return (
                    <li key={workflow.id} className="flex flex-wrap items-center justify-between gap-3 py-2">
                      <div>
                        <p className="flex items-center gap-2 font-medium">
                          <Badge tone={statusTone(workflow.status)}>{workflow.status}</Badge>
                          {dateTime(workflow.createdAt)}
                        </p>
                        <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">
                          {verificationSummary(workflow)}
                        </p>
                      </div>
                      {data.permissions.promote
                        && me.data?.role === 'admin'
                        && workflow.status === 'awaiting_promotion' && (
                          <Button
                            type="button"
                            variant="danger"
                            loading={promote.isPending}
                            disabled={!safetySet}
                            title={
                              safetySet
                                ? 'Overwrite production with this verified staging restore'
                                : 'Create a new successful production backup before promotion'
                            }
                            onClick={() => {
                              if (
                                safetySet
                                && window.confirm(
                                  'PROMOTE this verified staging restore to production? This overwrites production volumes and databases.',
                                )
                              ) {
                                promote.mutate({ workflow, safetySet });
                              }
                            }}
                          >
                            <ShieldCheck className="size-4" /> Promote
                          </Button>
                        )}
                    </li>
                  );
                })}
              </ul>
            )}
          </div>
        )}
        {(trigger.error ?? retry.error ?? promote.error) && (
          <ErrorNote error={trigger.error ?? retry.error ?? promote.error} />
        )}
      </div>

      {restoreSet && (
        <div className="border-t border-slate-200 px-5 py-4 dark:border-slate-800">
          <RestoreRequest
            subscriptionId={subscriptionId}
            portal={portal}
            set={restoreSet}
            onClose={() => setRestoreSet(null)}
          />
        </div>
      )}
    </Card>
  );
}
